/*!
 * pitchSieve.js — value | filter: random pitches through a pitch world
 * © 2026 Rob Canning — GPLv3
 *
 *     freq:Pwhite(40, 100) | scale(minor, root:A1)      the nearest note of A minor
 *     freq:Pwhite(40, 100) | notes([0, 3, 7], root:E1)  the nearest E, G or B (any octave)
 *     freq:Pwhite(40, 100) | notes([E, G, B])           ... the same, by name
 *     freq:Pwhite(40, 100) | edo(19)                    the nearest step of 19 equal (from A 440)
 *     freq:Pwhite(40, 100) | harm(27.5)                 the nearest overtone of 27.5 Hz: 55, 82.5 ...
 *     freq:Pwhite(40, 100) | sieve(3@0 + 4@1, root:C1)  Xenakis: semitones above the root that are
 *                                                       3n or 4n+1 (+ either, * both: 3@0 * 2@0)
 *     freq:Pwhite(40, 100) | scale(minor) | harm(55)    chained, left to right
 *     freq:Pwhite(40, 100) | scale(minor, even)         each note of the range as likely
 *
 * A value is drawn (a pattern's step, a rand(), a note), turned into a
 * pitch -- MIDI, with fractions -- and moved to the nearest one the filter
 * lets through, then on to the next. Pitch, not Hz: the grid is even in
 * every octave. Snapping favours a note with wide gaps round it (it
 * catches more draws); `even` instead picks among the notes the filters
 * let through between Pwhite's (or Pexprand's) ends, each as likely.
 *
 * Pure: values become Hz through the resolver synth.js gives it (pitchToHz),
 * so notes, m60, rand() all work as they do in freq:.
 */

import { scaleSteps, rootMidi } from "./scales.js";

const hzToMidi = hz => 69 + 12 * Math.log2(hz / 440);
const midiToHz = m => 440 * 2 ** ((m - 69) / 12);

let toHz = v => Number(v);                 // value -> Hz: synth.js sets pitchToHz
/** How a value (a note, m60, a rand()) becomes Hz: synth.js's pitchToHz. */
export function setPitchResolver(fn) { if (typeof fn === "function") toHz = fn; }

const REST = v => v == null || (typeof v === "string" && /^~?(r|rest)$/i.test(v));
const isNoteish = v => typeof v === "string" && /^~?([A-Ga-g][#b]?-?\d*|m-?\d)/.test(v);

/** A note (E, ~G2, Bb) as a pitch class 0..12 -- or null. Octave optional: E is any E. */
function pitchClass(v) {
  if (typeof v === "number") return ((v % 12) + 12) % 12;
  const s = String(v).trim();
  if (/^~?[A-Ga-g][#b]?$/.test(s)) return ((rootMidi(s + "4") % 12) + 12) % 12;
  if (isNoteish(s)) { const hz = toHz(s); return Number.isFinite(hz) && hz > 0 ? ((hzToMidi(hz) % 12) + 12) % 12 : null; }
  return null;
}

/** A filter's root as MIDI: a note, m57, MIDI, or Hz over 127 (rootMidi), its own default. */
const rootOf = (f, dflt) => (f.named.root != null ? rootMidi(f.named.root) : dflt);

/** The nearest of a periodic set: offsets within an octave (semitones above the root). */
function periodic(offsets, root) {
  const offs = [...new Set(offsets.map(o => ((o % 12) + 12) % 12))].sort((a, b) => a - b);
  if (!offs.length) return null;
  const at = (oct, i) => root + 12 * oct + offs[i];
  return {
    snap(m) {
      const oct = Math.floor((m - root) / 12);
      let best = m, d = Infinity;
      for (let o = oct - 1; o <= oct + 1; o++) for (let i = 0; i < offs.length; i++) {
        const p = at(o, i), e = Math.abs(p - m);
        if (e < d) { d = e; best = p; }
      }
      return best;
    },
    within(lo, hi) {
      const out = [];
      for (let o = Math.floor((lo - root) / 12) - 1; o <= Math.ceil((hi - root) / 12) + 1; o++) {
        for (let i = 0; i < offs.length; i++) { const p = at(o, i); if (p >= lo - 1e-9 && p <= hi + 1e-9) out.push(p); }
      }
      return out;
    },
  };
}

/** sieve(3@0 + 4@1 * 2@0): a union (+) of intersections (*) of residue classes m@r. */
export function parseSieve(text) {
  const terms = String(text ?? "").split("+").map(t => t.split("*").map(c => {
    const m = /^\s*(\d+)\s*@\s*(-?\d+)\s*$/.exec(c);
    return m && Number(m[1]) > 0 ? { mod: Number(m[1]), res: Number(m[2]) } : null;
  }));
  if (!terms.length || terms.some(t => t.some(c => !c))) return null;
  return n => terms.some(t => t.every(({ mod, res }) => (((n - res) % mod) + mod) % mod === 0));
}

/** One filter: { snap(midi) -> midi, within(lo, hi) -> [midi] } -- or null, said. */
function makeFilter(f) {
  const a0 = f.args[0];
  switch (f.name) {
    case "scale": return periodic(scaleSteps(a0 ?? "major"), rootOf(f, 60));
    case "notes": {
      const list = Array.isArray(a0) ? a0 : f.args;
      const root = rootOf(f, 60);
      const named = list.some(v => typeof v === "string" && !Number.isFinite(Number(v)));
      // names are where they are (E is E, whatever the root); numbers are above the root
      const offs = list.map(v => (named && typeof v === "string" && !Number.isFinite(Number(v)) ? pitchClass(v) - root : Number(v)))
        .filter(Number.isFinite);
      return periodic(offs, root);
    }
    case "edo": {
      const n = Number(a0);
      if (!(n > 0)) return null;
      const step = 12 / n, root = rootOf(f, 69);
      return {
        snap: m => root + Math.round((m - root) / step) * step,
        within(lo, hi) { const out = []; for (let k = Math.ceil((lo - root) / step - 1e-9); root + k * step <= hi + 1e-9; k++) out.push(root + k * step); return out; },
      };
    }
    case "harm": {
      // the fundamental: a number is Hz (harm(55)); a note its pitch (harm(A1))
      const f0 = typeof a0 === "number" ? a0 : toHz(a0);
      const max = Math.max(1, Math.round(Number(f.named.max ?? 64)));
      if (!(f0 > 0)) return null;
      const m0 = hzToMidi(f0);
      const mk = k => m0 + 12 * Math.log2(k);
      return {
        snap(m) {
          const k = clampK(2 ** ((m - m0) / 12), max);
          const lo = Math.max(1, Math.floor(k)), hi = Math.min(max, lo + 1);
          return Math.abs(mk(lo) - m) <= Math.abs(mk(hi) - m) ? mk(lo) : mk(hi);
        },
        within(lo, hi) { const out = []; for (let k = 1; k <= max; k++) { const p = mk(k); if (p > hi + 1e-9) break; if (p >= lo - 1e-9) out.push(p); } return out; },
      };
    }
    case "sieve": {
      const test = parseSieve(a0);
      if (!test) return null;
      const root = rootOf(f, 60);
      return {
        snap(m) {
          const x = m - root, n0 = Math.round(x);
          for (let d = 0; d <= 512; d++) {
            const cands = d ? [n0 - d, n0 + d].sort((p, q) => Math.abs(p - x) - Math.abs(q - x)) : [n0];
            for (const n of cands) if (test(n)) return root + n;
          }
          return m;                          // a sieve that lets nothing through: as it was
        },
        within(lo, hi) { const out = []; for (let n = Math.ceil(lo - root - 1e-9); root + n <= hi + 1e-9; n++) if (test(n)) out.push(root + n); return out; },
      };
    }
    default:
      return null;
  }
}
const clampK = (k, max) => Math.min(max, Math.max(1, k));

/** A pipe's filters, made once: [{ snap, within }] and whether `even` was asked. */
export function compilePipe(filters = []) {
  const made = [];
  let even = false;
  for (const f of filters) {
    const g = { name: String(f?.name ?? ""), args: f?.args ?? [], named: f?.named ?? {}, flags: f?.flags ?? [] };
    if (g.flags.includes("even") || g.args.includes("even")) even = true;
    g.args = g.args.filter(a => a !== "even");
    const made1 = makeFilter(g);
    if (made1) made.push(made1);
    else console.warn(`[pipe] ${g.name}(...) is not a pitch filter -- scale(), notes(), edo(), harm(), sieve()`);
  }
  return { filters: made, even };
}

/** A pitch (MIDI) through every filter, in order. */
export const snapMidi = (m, pipe) => pipe.filters.reduce((x, f) => f.snap(x), m);

/** Hz through the pipe -> Hz. */
export function pipeHz(hz, pipe) {
  if (!(hz > 0) || !pipe.filters.length) return hz;
  return midiToHz(snapMidi(hzToMidi(hz), pipe));
}

/** A value (a note, a number in Hz, a rand(), a chord, a rest) through the pipe: Hz, or the rest as it was. */
export function pipeValue(v, pipe) {
  if (REST(v)) return v;
  if (Array.isArray(v)) return v.map(x => pipeValue(x, pipe));
  const hz = toHz(v);
  return Number.isFinite(hz) ? pipeHz(hz, pipe) : v;
}

/**
 * `even`: one of the pitches the filters let through between lo and hi
 * (Hz), each as likely -- the first filter's, through the rest. Null when
 * none: the caller snaps instead.
 */
export function evenPick(loHz, hiHz, pipe, random = Math.random) {
  if (!(loHz > 0) || !(hiHz > 0) || !pipe.filters.length) return null;
  const lo = hzToMidi(Math.min(loHz, hiHz)), hi = hzToMidi(Math.max(loHz, hiHz));
  const [first, ...rest] = pipe.filters;
  const seen = new Set(), out = [];
  for (const p of first.within(lo, hi)) {
    const q = rest.reduce((x, f) => f.snap(x), p);
    const k = q.toFixed(6);
    if (!seen.has(k)) { seen.add(k); out.push(q); }
  }
  return out.length ? midiToHz(out[Math.floor(random() * out.length)]) : null;
}
